import { Outlet, Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard,
  Store,
  Inbox,
  HelpCircle,
  Users,
  BarChart3,
  Settings,
  LogOut,
  QrCode,
  ShieldAlert,
  ChevronRight,
} from "lucide-react";
import { useBossAuth } from "@/lib/contexts";

export default function BossShell() {
  const { boss, loading, logout } = useBossAuth();
  const loc = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center p-6 space-y-4">
        <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
        <p className="text-slate-400 text-sm font-mono">Verifying Boss Credentials...</p>
      </div>
    );
  }

  if (!boss) {
    return <Navigate to="/boss/login" replace />;
  }

  const navItems = [
    { label: "Dashboard", path: "/boss/dashboard", icon: LayoutDashboard },
    { label: "Cafes & Restaurants", path: "/boss/cafes", icon: Store },
    { label: "Leads / Inquiries", path: "/boss/leads", icon: Inbox },
    { label: "Support Requests", path: "/boss/support", icon: HelpCircle },
    { label: "Customer DB", path: "/boss/customers", icon: Users },
    { label: "Statistics", path: "/boss/stats", icon: BarChart3 },
    { label: "Settings", path: "/boss/settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen flex bg-slate-950 text-slate-100 font-body">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col justify-between shrink-0 hidden md:flex">
        <div className="p-6 space-y-8">
          {/* Brand Header */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary text-white grid place-items-center font-bold">
              <QrCode className="w-5 h-5 stroke-[2.2]" />
            </div>
            <div>
              <span className="font-heading text-xl font-bold text-white">Tablezy Boss</span>
              <span className="block text-[9px] font-mono text-primary uppercase tracking-widest">Admin Control</span>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = loc.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between px-4 py-3 rounded-2xl text-sm font-medium transition-all ${
                    active
                      ? "bg-primary text-white font-semibold shadow-lg shadow-primary/25"
                      : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </div>
                  {active && <ChevronRight className="w-4 h-4 opacity-70" />}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Footer Boss Account & Logout */}
        <div className="p-4 border-t border-slate-800 space-y-3">
          <div className="bg-slate-950 p-3 rounded-2xl border border-slate-800 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 text-primary grid place-items-center font-heading text-xs font-bold">
              {boss.name ? boss.name[0] : "B"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-white truncate">{boss.name || "Tablezy Boss"}</div>
              <div className="text-[10px] text-slate-500 font-mono truncate">{boss.email}</div>
            </div>
          </div>

          <button
            onClick={() => {
              logout();
              navigate("/boss/login");
            }}
            className="w-full h-10 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Logout Boss Session</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header Bar */}
        <header className="h-16 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4 md:hidden">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-primary text-white grid place-items-center font-bold">
              <QrCode className="w-4 h-4" />
            </div>
            <span className="font-heading font-bold text-white">Boss Panel</span>
          </div>

          <button
            onClick={() => {
              logout();
              navigate("/boss/login");
            }}
            className="text-xs text-red-400 font-mono"
          >
            Logout
          </button>
        </header>

        {/* Mobile Nav Horizontal Scroll */}
        <div className="flex md:hidden bg-slate-900/90 border-b border-slate-800 overflow-x-auto no-scrollbar p-2 gap-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap font-medium ${
                loc.pathname === item.path ? "bg-primary text-white" : "text-slate-400 bg-slate-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        {/* Dynamic Page View */}
        <main className="flex-1 p-6 sm:p-10 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
